import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, X, Newspaper, Eye, EyeOff, GripVertical, Image, Video } from 'lucide-react';
import { apiFetch } from '../lib/api';
import MediaLibraryPicker from '../components/admin/MediaLibraryPicker';

interface MediaCoverage {
  id: number;
  title: string;
  description?: string;
  imageUrl?: string;
  videoUrl?: string;
  link?: string;
  linkText?: string;
  type: string;
  typeLabel: string;
  date?: string;
  sortOrder: number;
  isActive: number;
  createdAt: string;
}

const TYPE_LABELS: Record<string, string> = {
  media: '媒體報導',
  video: '影片',
  brand: '品牌合作',
  graduate: '學員評價',
};

const emptyForm = {
  title: '',
  description: '',
  imageUrl: '',
  videoUrl: '',
  link: '',
  linkText: '了解更多',
  type: 'media',
  typeLabel: '媒體報導',
  date: '',
  sortOrder: 0,
  isActive: 1,
};

export default function AdminMediaCoverage() {
  const [items, setItems] = useState<MediaCoverage[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<'image' | 'video' | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    const res = await apiFetch<MediaCoverage[]>('/api/admin/media-coverage');
    if (res.success && res.data) {
      setItems(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item: MediaCoverage) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      description: item.description || '',
      imageUrl: item.imageUrl || '',
      videoUrl: item.videoUrl || '',
      link: item.link || '',
      linkText: item.linkText || '了解更多',
      type: item.type || 'media',
      typeLabel: item.typeLabel || '媒體報導',
      date: item.date || '',
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      alert('請輸入標題');
      return;
    }
    setSaving(true);
    const url = editingId
      ? `/api/admin/media-coverage/${editingId}`
      : '/api/admin/media-coverage';
    const method = editingId ? 'PUT' : 'POST';
    const res = await apiFetch(url, {
      method,
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (res.success) {
      setModalOpen(false);
      fetchItems();
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除這條報導嗎？')) return;
    const res = await apiFetch(`/api/admin/media-coverage/${id}`, { method: 'DELETE' });
    if (res.success) {
      fetchItems();
    } else {
      alert(res.error?.message || '刪除失敗');
    }
  };

  const toggleActive = async (item: MediaCoverage) => {
    const res = await apiFetch(`/api/admin/media-coverage/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: item.isActive ? 0 : 1 }),
    });
    if (res.success) fetchItems();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Newspaper className="w-6 h-6 text-brand-600" />
          媒體報導管理
        </h1>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          新增報導
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-4 py-3 text-left font-medium w-10"></th>
                <th className="px-4 py-3 text-left font-medium">標題</th>
                <th className="px-4 py-3 text-left font-medium">類別</th>
                <th className="px-4 py-3 text-left font-medium">連結</th>
                <th className="px-4 py-3 text-center font-medium">狀態</th>
                <th className="px-4 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <span className="inline-block w-full h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : items.length > 0 ? (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-300">
                      <GripVertical className="w-4 h-4" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.title}</div>
                      {item.description && (
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-700">
                        {item.typeLabel || TYPE_LABELS[item.type] || item.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {item.link ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-600 hover:underline truncate max-w-[150px] inline-block"
                        >
                          {item.linkText || item.link}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleActive(item)}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                          item.isActive
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={item.isActive ? '顯示中' : '已隱藏'}
                      >
                        {item.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-2 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                          title="編輯"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="刪除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                    暫無媒體報導，點擊「新增報導」開始添加
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editingId ? '編輯報導' : '新增報導'}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">標題 *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="例如：CTgoodjobs NGO 專訪"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">描述</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="簡短描述這條報導..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">類別</label>
                  <select
                    value={form.type}
                    onChange={(e) => {
                      const type = e.target.value;
                      const typeLabel = TYPE_LABELS[type] || type;
                      setForm((f) => ({ ...f, type, typeLabel }));
                    }}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm bg-white"
                  >
                    {Object.entries(TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">排序</label>
                  <input
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">日期</label>
                  <input
                    type="text"
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="2025年"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">圖片 URL</label>
                  <button
                    type="button"
                    onClick={() => { setPickerTarget('image'); setPickerOpen(true); }}
                    className="text-xs flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Image className="w-3.5 h-3.5" />
                    從媒體庫選擇
                  </button>
                </div>
                <input
                  type="text"
                  value={form.imageUrl}
                  onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="https://..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">連結 URL</label>
                  <input
                    type="text"
                    value={form.link}
                    onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">連結文字</label>
                  <input
                    type="text"
                    value={form.linkText}
                    onChange={(e) => setForm((f) => ({ ...f, linkText: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="閱讀專訪"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">影片 URL</label>
                  <button
                    type="button"
                    onClick={() => { setPickerTarget('video'); setPickerOpen(true); }}
                    className="text-xs flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Video className="w-3.5 h-3.5" />
                    從媒體庫選擇
                  </button>
                </div>
                <input
                  type="text"
                  value={form.videoUrl}
                  onChange={(e) => setForm((f) => ({ ...f, videoUrl: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="https://youtube.com/embed/..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={!!form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked ? 1 : 0 }))}
                  className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  前台顯示
                </label>
              </div>
            </div>
            <MediaLibraryPicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              filterType={pickerTarget || 'all'}
              onSelect={(url) => {
                if (pickerTarget === 'image') setForm((f) => ({ ...f, imageUrl: url }));
                else if (pickerTarget === 'video') setForm((f) => ({ ...f, videoUrl: url }));
              }}
            />
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
